import { z } from 'zod';
import { baseDocSchema, localeSchema, objectIdSchema, slugSchema } from './common.js';

// ISO-3166 alpha-2 country code. Always normalised to uppercase. Used to tell
// Stripe in which country to open the connected account (the country gets
// permanently locked at account creation time on Stripe's side).
export const countryCodeSchema = z
  .string()
  .length(2)
  .transform((v) => v.toUpperCase())
  .pipe(z.string().regex(/^[A-Z]{2}$/));
export type CountryCode = z.infer<typeof countryCodeSchema>;

/**
 * Países en los que abrimos Stripe Connect Express y donde activamente
 * invitamos a productoras. La lista vive en `shared` porque el API la usa
 * para validar lo que el geo-IP devuelve (y caer a `null` si el usuario
 * está en un país que aún no soportamos) y el Web la renderiza en el
 * formulario de onboarding.
 *
 * Si agregás un país, asegurate de que Stripe Connect Express lo soporte:
 *   https://stripe.com/global
 */
export interface SupportedCountry {
  code: string;
  flag: string;
  /**
   * Localised display names. Keys are the platform locale codes (see
   * `localeSchema`); extra keys are forwards-compatible. When a new locale is
   * added, append a name for every supported country.
   */
  name: Record<'es' | 'en', string>;
}

export const SUPPORTED_COUNTRIES: readonly SupportedCountry[] = [
  // LATAM
  { code: 'MX', flag: '🇲🇽', name: { es: 'México', en: 'Mexico' } },
  { code: 'AR', flag: '🇦🇷', name: { es: 'Argentina', en: 'Argentina' } },
  { code: 'BR', flag: '🇧🇷', name: { es: 'Brasil', en: 'Brazil' } },
  { code: 'CL', flag: '🇨🇱', name: { es: 'Chile', en: 'Chile' } },
  { code: 'CO', flag: '🇨🇴', name: { es: 'Colombia', en: 'Colombia' } },
  { code: 'PE', flag: '🇵🇪', name: { es: 'Perú', en: 'Peru' } },
  { code: 'UY', flag: '🇺🇾', name: { es: 'Uruguay', en: 'Uruguay' } },
  // North America
  { code: 'US', flag: '🇺🇸', name: { es: 'Estados Unidos', en: 'United States' } },
  { code: 'CA', flag: '🇨🇦', name: { es: 'Canadá', en: 'Canada' } },
  // Europe (Eurozone + UK + Ireland)
  { code: 'ES', flag: '🇪🇸', name: { es: 'España', en: 'Spain' } },
  { code: 'DE', flag: '🇩🇪', name: { es: 'Alemania', en: 'Germany' } },
  { code: 'FR', flag: '🇫🇷', name: { es: 'Francia', en: 'France' } },
  { code: 'IT', flag: '🇮🇹', name: { es: 'Italia', en: 'Italy' } },
  { code: 'NL', flag: '🇳🇱', name: { es: 'Países Bajos', en: 'Netherlands' } },
  { code: 'PT', flag: '🇵🇹', name: { es: 'Portugal', en: 'Portugal' } },
  { code: 'IE', flag: '🇮🇪', name: { es: 'Irlanda', en: 'Ireland' } },
  { code: 'GB', flag: '🇬🇧', name: { es: 'Reino Unido', en: 'United Kingdom' } },
  // Asia-Pacific
  { code: 'AU', flag: '🇦🇺', name: { es: 'Australia', en: 'Australia' } },
  { code: 'NZ', flag: '🇳🇿', name: { es: 'Nueva Zelanda', en: 'New Zealand' } },
  { code: 'JP', flag: '🇯🇵', name: { es: 'Japón', en: 'Japan' } },
  { code: 'SG', flag: '🇸🇬', name: { es: 'Singapur', en: 'Singapore' } },
] as const;

const SUPPORTED_COUNTRY_CODE_SET = new Set<string>(SUPPORTED_COUNTRIES.map((c) => c.code));

export function isSupportedCountry(code: string | null | undefined): boolean {
  if (!code) return false;
  return SUPPORTED_COUNTRY_CODE_SET.has(code.toUpperCase());
}

/**
 * Moneda de liquidación (settlement currency) por defecto de la cuenta Stripe
 * Connect Express del país. Stripe la fija al crear la cuenta y es la única
 * en la que el productor puede recibir payouts sin habilitar "additional
 * settlement currencies" en su dashboard.
 *
 * La usamos como la moneda en la que se cobra a los compradores cuando un
 * productor de ese país publica un evento. Hacerlo así garantiza que el
 * PaymentIntent con `on_behalf_of` + `transfer_data.destination` nunca falle
 * por mismatch de moneda — Stripe rechaza el PI si la `currency` no es la
 * default settlement currency de la cuenta conectada.
 *
 * Si en el futuro queremos permitir cobrar en USD desde otro país (audiencia
 * internacional), el productor debe activar USD como secondary settlement
 * currency en Stripe primero, y nosotros agregar una opción por-org.
 */
export const COUNTRY_DEFAULT_CURRENCY: Record<string, string> = {
  MX: 'MXN',
  AR: 'ARS',
  BR: 'BRL',
  CL: 'CLP',
  CO: 'COP',
  PE: 'PEN',
  UY: 'UYU',
  US: 'USD',
  CA: 'CAD',
  ES: 'EUR',
  DE: 'EUR',
  FR: 'EUR',
  IT: 'EUR',
  NL: 'EUR',
  PT: 'EUR',
  IE: 'EUR',
  GB: 'GBP',
  AU: 'AUD',
  NZ: 'NZD',
  JP: 'JPY',
  SG: 'SGD',
};

export function defaultCurrencyForCountry(code: string): string {
  const upper = code.toUpperCase();
  const currency = COUNTRY_DEFAULT_CURRENCY[upper];
  if (!currency) {
    throw new Error(`No default currency mapped for country code "${upper}"`);
  }
  return currency;
}

export const orgSettingsSchema = z.object({
  defaultLocale: localeSchema.default('es'),
  defaultCurrency: z.string().length(3).default('USD'),
  defaultServiceFeePct: z.number().min(0).max(50).default(0),
  brandColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  logoUrl: z.string().url().optional(),
  customDomain: z.string().optional(),
});
export type OrgSettings = z.infer<typeof orgSettingsSchema>;

export const organizationSchema = baseDocSchema.extend({
  name: z.string().min(2).max(120),
  slug: slugSchema,
  ownerUserId: objectIdSchema,
  country: countryCodeSchema,
  stripeAccountId: z.string().nullable().default(null),
  stripeOnboardingComplete: z.boolean().default(false),
  creditsBalance: z.number().int().nonnegative().default(0),
  freeMonthlyAllowance: z.number().int().nonnegative().default(50),
  freeMonthlyConsumed: z.number().int().nonnegative().default(0),
  freeMonthlyResetAt: z.coerce.date().optional(),
  settings: orgSettingsSchema,
  suspendedAt: z.coerce.date().nullable().optional(),
  suspensionReason: z.string().optional(),
  reviewStatus: z.enum(['pending', 'approved', 'rejected']).default('pending'),
  reviewRequestedAt: z.coerce.date().nullable().optional(),
  reviewedAt: z.coerce.date().nullable().optional(),
  reviewedByUserId: objectIdSchema.nullable().optional(),
  reviewNotes: z.string().optional(),
});
export type Organization = z.infer<typeof organizationSchema>;

export const createOrgInputSchema = z.object({
  name: z.string().min(2).max(120),
  slug: slugSchema,
  defaultLocale: localeSchema.default('es'),
  country: countryCodeSchema,
});
export type CreateOrgInput = z.infer<typeof createOrgInputSchema>;
